import { allMainColors, DoughnutChart, ScoreCard, ScoreCardProps } from "@foodpilot/foods";
import { ScoreVariation } from "src/context/EntityBasedScore/ScoreService";
import i18n from "src/i18n";
import { formatNumber } from "src/utils/formatting";
import EmptySetIcon from "src/assets/icons/empty-set.svg?react";
import { useTheme } from "@mui/material";
import { useEffect } from "react";

export type ScoreChartValues = {
    values: number[];
    labels: string[];
};

export interface ScoreChartProps {
    data?: ScoreChartValues;
    totalScore?: number | null;
    allScoreWithVariations: ScoreVariation[];
    selectedScore: ScoreVariation;
    isRefetching?: boolean;
    isLoading?: boolean;
    disableVariations?: boolean;
    withVariation?: boolean;
    withLegend?: boolean;
}

export const ScoreChart = (props: ScoreChartProps) => {
    const {
        data,
        totalScore,
        allScoreWithVariations,
        selectedScore,
        isRefetching,
        isLoading,
        disableVariations = false,
        withVariation = false,
        withLegend = true,
    } = props;
    const theme = useTheme();

    const validPrecision = Math.min(Math.max(selectedScore.precision, 1), 100) || 0;

    const variationOptions = allScoreWithVariations.map((variation) => ({
        ...variation,
        onClick: () => {
            variation.onClick();
        },
        disabled: disableVariations !== false && variation.variationId !== null,
    }));

    useEffect(() => {
        if (selectedScore.variationId !== null) {
            variationOptions.find((v) => v.variationId === selectedScore.variationId)?.onClick();
        }
    }, [selectedScore.variationId, variationOptions]);

    const scoreCardHeader: ScoreCardProps["header"] = {
        selectedItemId: selectedScore.id,
        options: variationOptions,
        variant: "secondary",
    };

    const buildChartValues = () => {
        if (!data) return { values: [], labels: [] };

        const validScoreEntries = data.values.flatMap((value, index) =>
            !isNaN(value) ?
                [
                    {
                        value: Number(value.toPrecision(validPrecision)),
                        label: data.labels[index],
                    },
                ]
            :   [],
        );

        return validScoreEntries.reduce<ScoreChartValues>(
            (scores, scoreElement) => ({
                values: [...scores.values, scoreElement.value],
                labels: [...scores.labels, scoreElement.label],
            }),
            { values: [], labels: [] },
        );
    };

    const { values, labels } = buildChartValues();
    const chartColors = Object.values(allMainColors).slice(0, values.length);

    const isLoadingData = isLoading || isRefetching;
    const emptySetIcon = values.length === 0 ? <EmptySetIcon /> : null;

    const chart = (
        <DoughnutChart
            values={values}
            labels={labels}
            colors={chartColors}
            isLoading={isLoadingData}
            displayTotal={`${formatNumber(Number(totalScore), i18n.resolvedLanguage, { maximumFractionDigits: validPrecision })}`}
            valuesUnit={selectedScore.unit}
            legend={withLegend ? { position: "bottom" } : undefined}
            valuesFormatterFn={(value) =>
                formatNumber(value, i18n.resolvedLanguage, { maximumFractionDigits: validPrecision })
            }
            spacing={5}
            borderWidth={2}
            borderColor={theme.palette.common.white}
        >
            {emptySetIcon}
        </DoughnutChart>
    );

    return (
        <>
            {withVariation ?
                <ScoreCard header={scoreCardHeader} disableFooter>
                    {chart}
                </ScoreCard>
            :   chart}
        </>
    );
};
